import type { QuestionListOrder, QuestionListSort } from '@course-authoring/shared-types';
import { CenterstageFilterChips, type CenterstageFilterChip } from '../centerstage/CenterstageFilterChips';
import { QUESTION_BANK_BROWSE_COPY as COPY } from './questionBankBrowseCopy';
import { QuestionBankFilterSelect } from './QuestionBankFilterSelect';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';

export interface QuestionBankSortOption {
  key: string;
  label: string;
  sort: QuestionListSort;
  order: QuestionListOrder;
}

/** Each sort has one direction, as on the page. */
export const QUESTION_BANK_SORTS: readonly QuestionBankSortOption[] = [
  { key: 'responses', label: 'Most responses', sort: 'responses', order: 'desc' },
  { key: 'createdAt', label: 'Recently added', sort: 'createdAt', order: 'desc' },
  { key: 'usedIn', label: 'Most used in lessons', sort: 'usedIn', order: 'desc' },
  { key: 'alphabetical', label: 'Alphabetical', sort: 'questionText', order: 'asc' },
];

export const sortOptionFor = (key: string): QuestionBankSortOption =>
  QUESTION_BANK_SORTS.find((option) => option.key === key) ?? QUESTION_BANK_SORTS[0]!;

export interface QuestionBankSortRowProps {
  styles: QuestionBankBrowseStyles;
  /** The page draws a quieter sort box and a rule under the row. */
  variant: 'page' | 'picker';
  sortKey: string;
  onSortChange: (key: string) => void;
  /** One per active filter, in the caller's order. */
  chips: readonly CenterstageFilterChip[];
  total: number;
  page: number;
  pageSize: number;
  /** Shown whenever a chip shows. */
  onShowAll: () => void;
}

/** "Sort by", one chip per active filter, the count in a live region, and "Show all questions". Owns no filter state. */
export function QuestionBankSortRow({ styles, variant, sortKey, onSortChange, chips, total, page, pageSize, onShowAll }: QuestionBankSortRowProps) {
  const b = styles.browse;
  return (
    <div style={variant === 'page' ? b.pageSortRow : b.sortRow}>
      <QuestionBankFilterSelect styles={b} name={COPY.sortLabel} value={sortKey} onChange={onSortChange} quiet={variant === 'page'}>
        {QUESTION_BANK_SORTS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
      </QuestionBankFilterSelect>
      <CenterstageFilterChips styles={styles.centerstage} chips={chips} />
      <span role="status" style={b.count}>{COPY.questionCount(total, page, pageSize)}</span>
      {chips.length > 0 ? <button type="button" style={b.showAll} onClick={onShowAll}>{COPY.showAll}</button> : null}
    </div>
  );
}
